import type { Point } from '../../shared/game/world-types.js';
import { seededRandom } from '../../shared/game/seeded-random.js';
import type { EncounterSite } from './encounter-sites.js';

export interface EncounterPost {
  /** Individual positions relative to the encounter's landmark, in roster order. */
  positions: Point[];
  /** A loose moving crew, or a stationary crew with selected perimeter walkers. */
  activity: 'patrol' | 'lookouts';
  /** Relative loop: patrol displacements, or a lookout's perimeter positions. */
  loop: Point[];
  lookouts?: number[];
}

const GOLDEN_ANGLE = 2.399963;
const points = (pairs: number[][]): Point[] => pairs.map(([x, z]) => ({ x, z }));

/**
 * A crew's loose formation: a sunflower spread with jitter, so no crew stands in a row and a crew of any size fills out evenly.
 * The site's route stays authored (it follows roads and work sites); the positions and the choice of lookouts follow the roster.
 * Sneaking roles are never picked as lookouts, since a hidden walker is no visible activity.
 */
export function formationFor(site: Pick<EncounterSite, 'id' | 'stance' | 'route'>, members: readonly string[]): EncounterPost {
  const random = seededRandom(`${site.id}/formation`), phase = random() * Math.PI * 2;
  const positions = members.map((_, i): Point => {
    const radius = 2.7 * Math.sqrt(i + .6), angle = phase + i * GOLDEN_ANGLE + (random() - .5) * .5;
    return { x: Math.round((Math.cos(angle) * radius * 1.15 + (random() - .5) * 1.2) * 10) / 10, z: Math.round((Math.sin(angle) * radius + (random() - .5) * 1.2) * 10) / 10 };
  });
  const post: EncounterPost = { positions, activity: site.stance, loop: points(site.route) };
  if (site.stance === 'lookouts') {
    const eligible = members.flatMap((role, i) => role === 'assassin' ? [] : [i]), count = Math.max(1, Math.min(Math.floor(members.length / 4), members.length - 1));
    const picked: number[] = [];
    while (picked.length < Math.min(count, eligible.length)) { const pick = eligible[Math.floor(random() * eligible.length)]; if (!picked.includes(pick)) picked.push(pick); }
    post.lookouts = picked.sort((a, b) => a - b);
  }
  return post;
}
